/**
 * questionMeta.js — Era inference and literature detection for EndoBoard questions
 */

/**
 * Determine the era of a question based on its year field.
 * @param {object} question - Question object with optional `year` field
 * @returns {"recent"|"modern"|"classic"|"unknown"}
 */
export function getEra(question) {
  const y = question?.year;
  if (!y || y === 'unknown' || y === '') return 'unknown';

  const num = parseInt(y, 10);
  if (isNaN(num)) return 'unknown';

  if (num >= 2020) return 'recent';
  if (num >= 2014) return 'modern';
  return 'classic';
}

/**
 * Era display labels and metadata
 */
export const ERA_OPTIONS = [
  { value: '',        label: 'All Eras' },
  { value: 'recent',  label: 'Recent (2020+)', emoji: '🟢' },
  { value: 'modern',  label: 'Modern (2014–19)', emoji: '🔵' },
  { value: 'classic', label: 'Classic (pre-2014)', emoji: '🟤' },
  { value: 'unknown', label: 'Unknown Year', emoji: '⚪' },
];

// ── Literature detection ─────────────────────────────────────────────────────

// Common endodontic journal abbreviations & references
const JOURNAL_PATTERNS = [
  /j\s*endod/i,
  /int\s*endod\s*j/i,
  /oral\s*surg/i,
  /dent\s*traumatol/i,
  /j\s*dent\s*res/i,
  /j\s*am\s*dent/i,
  /aust\s*endod\s*j/i,
  /endod\s*dent\s*traumatol/i,
  /j\s*prosthet\s*dent/i,
  /j\s*oral\s*rehabil/i,
  /j\s*clin\s*periodontol/i,
];

// "Author et al." pattern
const ET_AL_RE = /et\s+al\.?/i;

// Author (Year) or Author, Year pattern — e.g. "Schilder (1974)", "De Moor, 2002"
const AUTHOR_YEAR_RE = /[A-Z][a-z]+[\s,]+(?:19|20)\d{2}/;

// Question text starts with "Author, Author, & Author?" pattern (oral exam style)
const AUTHOR_QUESTION_RE = /^[A-Z][a-z]+[\s,]+(?:&\s+)?[A-Z][a-z]+/;

// "study" / "found that" / "reported" / "demonstrated" style references
const STUDY_KEYWORD_RE = /\b(?:study|studies|found\s+that|reported|demonstrated|concluded|showed\s+that|according\s+to|literature\s+review)\b/i;

/**
 * Determine if a question is literature/study-based.
 * Checks question text, explanation, and source for author names, journal refs, etc.
 * @param {object} question
 * @returns {boolean}
 */
export function isLiteratureQuestion(question) {
  const q = question?.question || '';
  const expl = question?.explanation || '';
  const src = question?.source || '';
  const combined = `${q} ${expl} ${src}`;

  // Check for "et al."
  if (ET_AL_RE.test(combined)) return true;

  // Check for journal references
  if (JOURNAL_PATTERNS.some((re) => re.test(combined))) return true;

  // Check for Author (Year) citation patterns in explanation
  if (AUTHOR_YEAR_RE.test(expl)) return true;

  // Check if question itself is an author-name question (common oral exam style)
  if (AUTHOR_QUESTION_RE.test(q) && q.includes('?')) return true;

  // Check for study keywords in explanation (more specific)
  if (STUDY_KEYWORD_RE.test(expl) && AUTHOR_YEAR_RE.test(combined)) return true;

  // Check source for literature indicators
  if (/LIT/i.test(src)) return true;

  return false;
}

/**
 * Filter questions by era, category, sourceGroup, and literature toggle.
 * @param {object[]} questions - Array of question objects
 * @param {object} filters
 * @param {string} [filters.era] - "recent"|"modern"|"classic"|"unknown"|"" (empty = all)
 * @param {string} [filters.category] - Category string or "" for all
 * @param {string} [filters.sourceGroup] - Source string or "" for all
 * @param {boolean} [filters.literatureOnly] - If true, only return literature questions
 * @returns {object[]}
 */
export function getFilteredQuestions(questions, filters = {}) {
  const { era, category, sourceGroup, literatureOnly } = filters;

  return questions.filter((q) => {
    if (era && getEra(q) !== era) return false;
    if (category && q.category !== category) return false;
    if (sourceGroup && q.source !== sourceGroup) return false;
    if (literatureOnly && !isLiteratureQuestion(q)) return false;
    return true;
  });
}
